import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '../../core/config/app-config';
import { AuthApiResponse } from '../../features/auth/domain/models/social-registration.model';
import { AuthSessionService } from './auth-session.service';

class TokenRefreshError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenRefreshError';
  }
}

/**
 * Ejecuta las operaciones de ciclo de vida del JWT sin pasar por interceptores.
 * Así la renovación no se intercepta a sí misma.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly config = inject(APP_CONFIG);
  private readonly authSession = inject(AuthSessionService);
  private refreshInFlight: Promise<string> | null = null;

  /**
   * Renueva el JWT actual. Las peticiones simultáneas comparten la misma renovación
   * para no invalidar el token entre ellas.
   */
  refresh(): Promise<string> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const currentToken = this.authSession.token();
    if (!currentToken) {
      return Promise.reject(new TokenRefreshError('No hay un token de sesión para renovar.'));
    }

    this.refreshInFlight = this.requestRefresh(currentToken).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  /** Invalida el JWT en el backend y siempre borra la sesión de esta pestaña. */
  async logout(): Promise<void> {
    const currentToken = this.authSession.token();

    try {
      if (!currentToken) {
        return;
      }

      const response = await firstValueFrom(
        this.http.delete<AuthApiResponse<unknown>>(this.endpoint('/api/autenticacion/eliminar-token'), {
          headers: this.authorizationHeaders(currentToken),
        }),
      );

      if (this.hasApiError(response)) {
        throw new Error(response.mensaje || 'No fue posible cerrar la sesión en el servidor.');
      }
    } finally {
      this.authSession.clear();
    }
  }

  private async requestRefresh(currentToken: string): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.http.post<AuthApiResponse<unknown>>(
          this.endpoint('/api/autenticacion/refrescar-token'),
          null,
          { headers: this.authorizationHeaders(currentToken) },
        ),
      );

      if (this.hasApiError(response)) {
        throw new TokenRefreshError(response.mensaje || 'El servidor rechazó la renovación del token.');
      }

      const refreshedToken = this.extractToken(response);
      if (!refreshedToken) {
        throw new TokenRefreshError('El servidor no devolvió un token JWT actualizado.');
      }

      this.authSession.updateToken(refreshedToken);
      return refreshedToken;
    } catch (error) {
      if (this.mustClearSession(error)) {
        this.authSession.clear();
      }

      throw error;
    }
  }

  private endpoint(path: string): string {
    return `${this.config.authApiBaseUrl.replace(/\/$/, '')}${path}`;
  }

  private authorizationHeaders(token: string): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private hasApiError(response: AuthApiResponse<unknown>): boolean {
    return typeof response.estatus === 'string' && response.estatus.toUpperCase() === 'ERROR';
  }

  /** El OpenAPI declara dto como genérico; se admiten las formas usadas por el backend. */
  private extractToken(response: AuthApiResponse<unknown>): string | null {
    return this.extractTokenFrom(response.dto) ?? this.extractTokenFrom(response);
  }

  private extractTokenFrom(value: unknown): string | null {
    if (typeof value === 'string') {
      return value.trim() || null;
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const candidateFields = ['tokenJWT', 'tokenJwt', 'token', 'jwt'];
    const record = value as Record<string, unknown>;

    for (const field of candidateFields) {
      const candidate = record[field];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  }

  private mustClearSession(error: unknown): boolean {
    return (
      error instanceof TokenRefreshError ||
      (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403))
    );
  }
}
